import { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient from '../../api/apiClient';

const normalizeItems = (items = []) => {
    return [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
};

const normalizeSections = (sections = []) => {
    const sorted = [...sections].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const byKey = sorted.reduce((acc, section) => {
        acc[section.section_key] = {
            ...section,
            Items: normalizeItems(section.Items || []),
        };
        return acc;
    }, {});
    return { byKey, sorted: sorted.map(s => ({ ...s, Items: normalizeItems(s.Items || []) })) };
};

const withSectionFallback = (section, fallback = {}) => {
    if (!section) return fallback;

    return {
        ...fallback,
        ...section,
        title: section.title ?? fallback.title,
        title_highlight: section.title_highlight ?? fallback.title_highlight,
        subtitle: section.subtitle ?? fallback.subtitle,
        description: section.description ?? fallback.description,
        button_text: section.button_text ?? fallback.button_text,
        button_link: section.button_link ?? fallback.button_link,
        background_image: section.background_image ?? fallback.background_image,
        Items: section.Items?.length ? section.Items : (fallback.Items || fallback.items || []),
    };
};

const useFrontendPageContent = (pageKey) => {
    const [pageContent, setPageContent] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let mounted = true;
        setLoading(true);

        apiClient.get(`/web-pages/frontend-pages/${pageKey}`)
            .then((response) => {
                if (mounted) setPageContent(response.data.data || null);
            })
            .catch(() => {
                if (mounted) setPageContent(null);
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [pageKey]);

    const normalized = useMemo(() => normalizeSections(pageContent?.Sections || []), [pageContent]);
    const sectionsByKey = normalized.byKey;
    const sections = normalized.sorted;

    const getSection = useCallback((sectionKey, fallback = {}) => {
        return withSectionFallback(sectionsByKey[sectionKey], fallback);
    }, [sectionsByKey]);

    const isSectionActive = useCallback((sectionKey) => {
        return !!sectionsByKey[sectionKey];
    }, [sectionsByKey]);

    return {
        pageContent,
        loading,
        sections,
        getSection,
        isSectionActive,
    };
};

export default useFrontendPageContent;
